import type {
  BasicStudyMaCrossLineType,
  BasicStudyMaType,
  BasicStudySource,
  Candle,
  CandleInterval,
  OverlayLineStyle,
  PivotPointsLabelPosition,
  PivotPointsLevel,
  PivotPointsTimeframe,
  PivotPointsType,
} from '../types';
import {
  BASIC_INDICATOR_DEFINITIONS,
  BASIC_INDICATOR_KEYS,
  type BasicIndicatorKeyRaw,
} from '../indicatorRegistry';

export interface BasicStudySettings {
  inputs: {
    length: number;
    length2: number;
    length3: number;
    length4: number;
    multiplier: number;
    maCrossLine1Type?: BasicStudyMaCrossLineType;
    maCrossLine2Type?: BasicStudyMaCrossLineType;
    maCrossLine1OffsetPercent?: number;
    maCrossLine2OffsetPercent?: number;
    pivotPointsType?: PivotPointsType;
    pivotPointsTimeframe?: PivotPointsTimeframe;
    pivotPointsBack?: number;
    pivotPointsUseDailyBasedValues?: boolean;
    pivotPointsShowLabels?: boolean;
    pivotPointsShowPrices?: boolean;
    pivotPointsLabelsPosition?: PivotPointsLabelPosition;
  };
  style: {
    primaryColor: string;
    secondaryColor: string;
    tertiaryColor: string;
    positiveColor: string;
    negativeColor: string;
    lineWidth: 1 | 2 | 3 | 4;
    lineStyle: OverlayLineStyle;
    pivotLevelsColor?: Partial<Record<PivotPointsLevel, string>>;
  };
  visibility: {
    showPrimary: boolean;
    showSecondary: boolean;
    showTertiary: boolean;
    showHistogram: boolean;
    showSeriesLabels: boolean;
    pivotLevels?: Partial<Record<PivotPointsLevel, boolean>>;
  };
}

const DEFAULT_MA_CROSS_LINE_1_TYPE: BasicStudyMaCrossLineType = 'ema';
const DEFAULT_MA_CROSS_LINE_2_TYPE: BasicStudyMaCrossLineType = 'ema';
const DEFAULT_MA_CROSS_LINE_1_OFFSET_PERCENT = 0;
const DEFAULT_MA_CROSS_LINE_2_OFFSET_PERCENT = 0;
const PIVOT_POINTS_LEVELS: PivotPointsLevel[] = ['p', 'r1', 'r2', 'r3', 'r4', 'r5', 's1', 's2', 's3', 's4', 's5'];
const DEFAULT_PIVOT_POINTS_TYPE: PivotPointsType = 'traditional';
const DEFAULT_PIVOT_POINTS_TIMEFRAME: PivotPointsTimeframe = 'auto';
const DEFAULT_PIVOT_POINTS_BACK = 15;
const DEFAULT_PIVOT_POINTS_USE_DAILY_BASED_VALUES = true;
const DEFAULT_PIVOT_POINTS_SHOW_LABELS = true;
const DEFAULT_PIVOT_POINTS_SHOW_PRICES = true;
const DEFAULT_PIVOT_POINTS_LABELS_POSITION: PivotPointsLabelPosition = 'right';
export const DEFAULT_PIVOT_LEVELS_COLOR: Record<PivotPointsLevel, string> = {
  p: '#f59e0b',
  r1: '#ef4444',
  r2: '#dc2626',
  r3: '#b91c1c',
  r4: '#991b1b',
  r5: '#7f1d1d',
  s1: '#22c55e',
  s2: '#16a34a',
  s3: '#15803d',
  s4: '#166534',
  s5: '#14532d',
};
export const DEFAULT_PIVOT_LEVELS_VISIBILITY: Record<PivotPointsLevel, boolean> = {
  p: true,
  r1: true,
  r2: true,
  r3: true,
  r4: true,
  r5: true,
  s1: true,
  s2: true,
  s3: true,
  s4: true,
  s5: true,
};

interface PivotPeriodCandle {
  startMs: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface BasicStudyComputation {
  primary: Array<number | undefined>;
  secondary: Array<number | undefined>;
  tertiary: Array<number | undefined>;
  histogram: Array<number | undefined>;
  pivotLevels?: Partial<Record<PivotPointsLevel, Array<number | undefined>>>;
}

export interface BasicStudyContext {
  visibleRange?: {
    fromIndex: number;
    toIndex: number;
  };
  breadthByTimestamp?: Record<string, { advances: number; declines: number; advancingVolume: number; decliningVolume: number }>;
  chartInterval?: CandleInterval;
  dailyCandles?: Candle[];
  weeklyCandles?: Candle[];
  monthlyCandles?: Candle[];
}

function clampInteger(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}

function closeValues(candles: Candle[]): number[] {
  return candles.map((candle) => candle.close);
}

function sourceValues(candles: Candle[], source: BasicStudySource): number[] {
  if (source === 'open') {
    return candles.map((candle) => candle.open);
  }
  if (source === 'high') {
    return candles.map((candle) => candle.high);
  }
  if (source === 'low') {
    return candles.map((candle) => candle.low);
  }
  if (source === 'hl2') {
    return candles.map((candle) => (candle.high + candle.low) / 2);
  }
  if (source === 'hlc3') {
    return candles.map((candle) => (candle.high + candle.low + candle.close) / 3);
  }
  if (source === 'ohlc4') {
    return candles.map((candle) => (candle.open + candle.high + candle.low + candle.close) / 4);
  }
  if (source === 'hlcc4') {
    return candles.map((candle) => (candle.high + candle.low + candle.close + candle.close) / 4);
  }
  return closeValues(candles);
}

function hl2Values(candles: Candle[]): number[] {
  return candles.map((candle) => (candle.high + candle.low) / 2);
}

function sma(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);
  let sum = 0;

  for (let index = 0; index < values.length; index += 1) {
    sum += values[index];
    if (index >= period) {
      sum -= values[index - period];
    }
    if (index >= period - 1) {
      output[index] = sum / period;
    }
  }

  return output;
}

function ema(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);
  if (values.length < period) {
    return output;
  }

  let seed = 0;
  for (let index = 0; index < period; index += 1) {
    seed += values[index];
  }

  let current = seed / period;
  output[period - 1] = current;
  const multiplier = 2 / (period + 1);

  for (let index = period; index < values.length; index += 1) {
    current += (values[index] - current) * multiplier;
    output[index] = current;
  }

  return output;
}

function emaMaybe(values: Array<number | undefined>, length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);

  const seed: number[] = [];
  let current: number | null = null;
  const multiplier = 2 / (period + 1);

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (value === undefined || !Number.isFinite(value)) {
      continue;
    }

    if (current === null) {
      seed.push(value);
      if (seed.length < period) {
        continue;
      }
      current = seed.reduce((sum, item) => sum + item, 0) / period;
      output[index] = current;
      continue;
    }

    current += (value - current) * multiplier;
    output[index] = current;
  }

  return output;
}

function rma(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);
  if (values.length < period) {
    return output;
  }

  let seed = 0;
  for (let index = 0; index < period; index += 1) {
    seed += values[index];
  }

  let current = seed / period;
  output[period - 1] = current;

  for (let index = period; index < values.length; index += 1) {
    current = (current * (period - 1) + values[index]) / period;
    output[index] = current;
  }

  return output;
}

function wma(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);
  const denominator = (period * (period + 1)) / 2;

  for (let index = period - 1; index < values.length; index += 1) {
    let weightedSum = 0;
    for (let offset = 0; offset < period; offset += 1) {
      const weight = period - offset;
      weightedSum += values[index - offset] * weight;
    }
    output[index] = weightedSum / denominator;
  }

  return output;
}

function vwma(values: number[], volumes: number[], length: number): Array<number | undefined> {
  const pv = values.map((value, index) => value * volumes[index]);
  const sumPv = rollingSum(pv, length);
  const sumVol = rollingSum(volumes, length);

  return values.map((_, index) => {
    const numerator = sumPv[index];
    const denominator = sumVol[index];
    if (numerator === undefined || denominator === undefined || denominator === 0) {
      return undefined;
    }
    return numerator / denominator;
  });
}

function movingAverageByType(
  values: number[],
  volumes: number[],
  length: number,
  maType: BasicStudyMaType,
): Array<number | undefined> {
  if (maType === 'sma') {
    return sma(values, length);
  }
  if (maType === 'smma') {
    return rma(values, length);
  }
  if (maType === 'wma') {
    return wma(values, length);
  }
  if (maType === 'vwma') {
    return vwma(values, volumes, length);
  }
  return ema(values, length);
}

function maCrossMovingAverageByType(
  values: number[],
  length: number,
  maType: BasicStudyMaCrossLineType,
): Array<number | undefined> {
  if (maType === 'ema') {
    return ema(values, length);
  }
  return sma(values, length);
}

function applyOffsetPercent(values: Array<number | undefined>, offsetPercent: number): Array<number | undefined> {
  const normalizedOffsetPercent = Math.max(-100, Math.min(100, offsetPercent));
  const multiplier = 1 + normalizedOffsetPercent / 100;

  return values.map((value) => {
    if (value === undefined) {
      return undefined;
    }
    return value * multiplier;
  });
}

function highest(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);

  for (let index = period - 1; index < values.length; index += 1) {
    let maxValue = values[index];
    for (let offset = 1; offset < period; offset += 1) {
      const candidate = values[index - offset];
      if (candidate > maxValue) {
        maxValue = candidate;
      }
    }
    output[index] = maxValue;
  }

  return output;
}

function lowest(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);

  for (let index = period - 1; index < values.length; index += 1) {
    let minValue = values[index];
    for (let offset = 1; offset < period; offset += 1) {
      const candidate = values[index - offset];
      if (candidate < minValue) {
        minValue = candidate;
      }
    }
    output[index] = minValue;
  }

  return output;
}

function rollingSum(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);
  let sum = 0;

  for (let index = 0; index < values.length; index += 1) {
    sum += values[index];
    if (index >= period) {
      sum -= values[index - period];
    }
    if (index >= period - 1) {
      output[index] = sum;
    }
  }

  return output;
}

function stddev(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);
  const basis = sma(values, period);

  for (let index = period - 1; index < values.length; index += 1) {
    const average = basis[index];
    if (average === undefined) {
      continue;
    }

    let sum = 0;
    for (let offset = 0; offset < period; offset += 1) {
      const delta = values[index - offset] - average;
      sum += delta * delta;
    }
    output[index] = Math.sqrt(sum / period);
  }

  return output;
}

function trueRange(candles: Candle[]): number[] {
  return candles.map((candle, index) => {
    if (index === 0) {
      return candle.high - candle.low;
    }
    const previousClose = candles[index - 1].close;
    const highLow = candle.high - candle.low;
    const highClose = Math.abs(candle.high - previousClose);
    const lowClose = Math.abs(candle.low - previousClose);
    return Math.max(highLow, highClose, lowClose);
  });
}

function computeRsi(values: number[], length: number): Array<number | undefined> {
  const period = clampInteger(length, 1, 1000);
  const output: Array<number | undefined> = new Array(values.length).fill(undefined);
  if (values.length <= period) {
    return output;
  }

  let gains = 0;
  let losses = 0;
  for (let index = 1; index <= period; index += 1) {
    const delta = values[index] - values[index - 1];
    if (delta >= 0) {
      gains += delta;
    } else {
      losses += -delta;
    }
  }

  let avgGain = gains / period;
  let avgLoss = losses / period;
  output[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  for (let index = period + 1; index < values.length; index += 1) {
    const delta = values[index] - values[index - 1];
    const gain = delta > 0 ? delta : 0;
    const loss = delta < 0 ? -delta : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    output[index] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }

  return output;
}

function safeDivide(numerator: number, denominator: number): number | undefined {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
    return undefined;
  }
  const result = numerator / denominator;
  return Number.isFinite(result) ? result : undefined;
}

function sanitizeSeries(values: Array<number | undefined>): Array<number | undefined> {
  return values.map((value) => (value === undefined || !Number.isFinite(value) ? undefined : value));
}

function seededUnit(key: string): number {
  let hash = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 0) % 10000) / 10000;
}

function seriesDifference(
  left: Array<number | undefined>,
  right: Array<number | undefined>,
): Array<number | undefined> {
  return left.map((value, index) => {
    const other = right[index];
    if (value === undefined || other === undefined) {
      return undefined;
    }
    return value - other;
  });
}

function signedVolumeCumulative(candles: Candle[]): Array<number | undefined> {
  let cumulative = 0;
  return candles.map((candle, index) => {
    if (index === 0) {
      return 0;
    }
    const previous = candles[index - 1].close;
    if (candle.close > previous) {
      cumulative += candle.volume;
    } else if (candle.close < previous) {
      cumulative -= candle.volume;
    }
    return cumulative;
  });
}

interface SwingPivot {
  index: number;
  price: number;
  kind: 'high' | 'low';
}

function parseCandleTime(candle: Candle): Date | null {
  const timestamp = Date.parse(candle.time);
  if (!Number.isFinite(timestamp)) {
    return null;
  }
  return new Date(timestamp);
}

function candleTimestampKey(candle: Candle): string | null {
  const timestamp = Date.parse(candle.time);
  if (!Number.isFinite(timestamp)) {
    return null;
  }
  return String(Math.round(timestamp));
}

function utcDayKey(candle: Candle): string {
  const timestamp = parseCandleTime(candle);
  if (!timestamp) {
    return candle.time.slice(0, 10);
  }
  return `${timestamp.getUTCFullYear()}-${timestamp.getUTCMonth() + 1}-${timestamp.getUTCDate()}`;
}

function utcDayOfYear(candle: Candle): number {
  const timestamp = parseCandleTime(candle);
  if (!timestamp) {
    return 1;
  }
  const year = timestamp.getUTCFullYear();
  const dayStart = Date.UTC(year, timestamp.getUTCMonth(), timestamp.getUTCDate());
  const yearStart = Date.UTC(year, 0, 1);
  return Math.floor((dayStart - yearStart) / 86_400_000) + 1;
}

function isDailyOrHigherInterval(interval: CandleInterval): boolean {
  return interval === '1d' || interval === '1w' || interval === '1M';
}

function resolvePivotPointsTimeframe(
  timeframe: PivotPointsTimeframe | undefined,
  chartInterval: CandleInterval,
): Exclude<PivotPointsTimeframe, 'auto'> {
  if (timeframe && timeframe !== 'auto') {
    return timeframe;
  }

  if (chartInterval === '1m' || chartInterval === '3m' || chartInterval === '5m' || chartInterval === '15m') {
    return 'daily';
  }

  if (
    chartInterval === '30m' ||
    chartInterval === '45m' ||
    chartInterval === '1h' ||
    chartInterval === '2h' ||
    chartInterval === '3h' ||
    chartInterval === '4h'
  ) {
    return 'weekly';
  }

  return 'monthly';
}

function startMsForPivotPeriod(timeMs: number, timeframe: Exclude<PivotPointsTimeframe, 'auto'>): number {
  const date = new Date(timeMs);
  if (timeframe === 'daily') {
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  if (timeframe === 'weekly') {
    const day = (date.getUTCDay() + 6) % 7;
    const monday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    monday.setUTCDate(monday.getUTCDate() - day);
    return monday.getTime();
  }

  if (timeframe === 'monthly') {
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
  }

  if (timeframe === 'quarterly') {
    const quarterMonth = Math.floor(date.getUTCMonth() / 3) * 3;
    return Date.UTC(date.getUTCFullYear(), quarterMonth, 1);
  }

  return Date.UTC(date.getUTCFullYear(), 0, 1);
}

function aggregateCandlesByPivotPeriod(
  candles: Candle[],
  timeframe: Exclude<PivotPointsTimeframe, 'auto'>,
): PivotPeriodCandle[] {
  const sorted = candles
    .flatMap((candle) => {
      const timeMs = Date.parse(candle.time);
      if (
        !Number.isFinite(timeMs) ||
        !Number.isFinite(candle.open) ||
        !Number.isFinite(candle.high) ||
        !Number.isFinite(candle.low) ||
        !Number.isFinite(candle.close)
      ) {
        return [];
      }
      return [{ candle, timeMs }];
    })
    .sort((left, right) => left.timeMs - right.timeMs);

  const periods: PivotPeriodCandle[] = [];
  for (const entry of sorted) {
    const startMs = startMsForPivotPeriod(entry.timeMs, timeframe);
    const previous = periods[periods.length - 1];
    if (previous && previous.startMs === startMs) {
      previous.high = Math.max(previous.high, entry.candle.high);
      previous.low = Math.min(previous.low, entry.candle.low);
      previous.close = entry.candle.close;
      continue;
    }

    periods.push({
      startMs,
      open: entry.candle.open,
      high: entry.candle.high,
      low: entry.candle.low,
      close: entry.candle.close,
    });
  }

  return periods;
}

function computePivotPointLevels(
  type: PivotPointsType,
  previous: PivotPeriodCandle,
  currentOpen: number,
): Partial<Record<PivotPointsLevel, number>> {
  const prevHigh = previous.high;
  const prevLow = previous.low;
  const prevClose = previous.close;
  const prevOpen = previous.open;
  const range = prevHigh - prevLow;

  if (!Number.isFinite(prevHigh) || !Number.isFinite(prevLow) || !Number.isFinite(prevClose) || !Number.isFinite(prevOpen)) {
    return {};
  }

  if (type === 'fibonacci') {
    const p = (prevHigh + prevLow + prevClose) / 3;
    return {
      p,
      r1: p + 0.382 * range,
      s1: p - 0.382 * range,
      r2: p + 0.618 * range,
      s2: p - 0.618 * range,
      r3: p + range,
      s3: p - range,
    };
  }

  if (type === 'woodie') {
    if (!Number.isFinite(currentOpen)) {
      return {};
    }
    const p = (prevHigh + prevLow + 2 * currentOpen) / 4;
    const r3 = prevHigh + 2 * (p - prevLow);
    const s3 = prevLow - 2 * (prevHigh - p);
    return {
      p,
      r1: 2 * p - prevLow,
      s1: 2 * p - prevHigh,
      r2: p + range,
      s2: p - range,
      r3,
      s3,
      r4: r3 + range,
      s4: s3 - range,
    };
  }

  if (type === 'classic') {
    const p = (prevHigh + prevLow + prevClose) / 3;
    return {
      p,
      r1: 2 * p - prevLow,
      s1: 2 * p - prevHigh,
      r2: p + range,
      s2: p - range,
      r3: p + 2 * range,
      s3: p - 2 * range,
      r4: p + 3 * range,
      s4: p - 3 * range,
    };
  }

  if (type === 'dm') {
    let x = prevHigh + prevLow + 2 * prevClose;
    if (Math.abs(prevOpen - prevClose) > 1e-12) {
      x = prevClose > prevOpen ? 2 * prevHigh + prevLow + prevClose : 2 * prevLow + prevHigh + prevClose;
    }
    return {
      p: x / 4,
      r1: x / 2 - prevLow,
      s1: x / 2 - prevHigh,
    };
  }

  if (type === 'camarilla') {
    const p = (prevHigh + prevLow + prevClose) / 3;
    const rangeScaled = 1.1 * range;
    const r5 = prevLow === 0 ? undefined : (prevHigh / prevLow) * prevClose;
    return {
      p,
      r1: prevClose + rangeScaled / 12,
      s1: prevClose - rangeScaled / 12,
      r2: prevClose + rangeScaled / 6,
      s2: prevClose - rangeScaled / 6,
      r3: prevClose + rangeScaled / 4,
      s3: prevClose - rangeScaled / 4,
      r4: prevClose + rangeScaled / 2,
      s4: prevClose - rangeScaled / 2,
      r5,
      s5: r5 === undefined ? undefined : prevClose - (r5 - prevClose),
    };
  }

  const p = (prevHigh + prevLow + prevClose) / 3;
  return {
    p,
    r1: 2 * p - prevLow,
    s1: 2 * p - prevHigh,
    r2: p + range,
    s2: p - range,
    r3: 2 * p + (prevHigh - 2 * prevLow),
    s3: 2 * p - (2 * prevHigh - prevLow),
    r4: 3 * p + (prevHigh - 3 * prevLow),
    s4: 3 * p - (3 * prevHigh - prevLow),
    r5: 4 * p + (prevHigh - 4 * prevLow),
    s5: 4 * p - (4 * prevHigh - prevLow),
  };
}

function computePivotPointsStandardLevels(
  candles: Candle[],
  settings: BasicStudySettings,
  context: BasicStudyContext | undefined,
): Partial<Record<PivotPointsLevel, Array<number | undefined>>> {
  const chartInterval = context?.chartInterval ?? '1h';
  const resolvedTimeframe = resolvePivotPointsTimeframe(settings.inputs.pivotPointsTimeframe, chartInterval);
  const pivotType = settings.inputs.pivotPointsType ?? DEFAULT_PIVOT_POINTS_TYPE;
  const pivotsBack = clampInteger(settings.inputs.pivotPointsBack ?? DEFAULT_PIVOT_POINTS_BACK, 1, 200);
  const useDailyBasedValues =
    (settings.inputs.pivotPointsUseDailyBasedValues ?? DEFAULT_PIVOT_POINTS_USE_DAILY_BASED_VALUES) &&
    !isDailyOrHigherInterval(chartInterval);

  let sourceCandles = candles;
  if (useDailyBasedValues) {
    if (resolvedTimeframe === 'daily') {
      sourceCandles = context?.dailyCandles ?? candles;
    } else if (resolvedTimeframe === 'weekly') {
      sourceCandles = context?.weeklyCandles ?? candles;
    } else {
      sourceCandles = context?.monthlyCandles ?? candles;
    }
  }

  const sourcePeriods = aggregateCandlesByPivotPeriod(sourceCandles, resolvedTimeframe);
  if (sourcePeriods.length < 2) {
    return {};
  }

  const pivotSeries = Object.fromEntries(
    PIVOT_POINTS_LEVELS.map((level) => [level, new Array(candles.length).fill(undefined)]),
  ) as Record<PivotPointsLevel, Array<number | undefined>>;
  const periodIndexByStartMs = new Map<number, number>();
  for (let index = 0; index < sourcePeriods.length; index += 1) {
    periodIndexByStartMs.set(sourcePeriods[index].startMs, index);
  }

  const visibleFromPeriodIndex = Math.max(0, sourcePeriods.length - pivotsBack);
  const periodLevelsCache = new Map<number, Partial<Record<PivotPointsLevel, number>>>();

  for (let index = 0; index < candles.length; index += 1) {
    const timeMs = Date.parse(candles[index].time);
    if (!Number.isFinite(timeMs)) {
      continue;
    }

    const startMs = startMsForPivotPeriod(timeMs, resolvedTimeframe);
    const periodIndex = periodIndexByStartMs.get(startMs);
    if (periodIndex === undefined || periodIndex === 0 || periodIndex < visibleFromPeriodIndex) {
      continue;
    }

    let levels = periodLevelsCache.get(periodIndex);
    if (!levels) {
      const previous = sourcePeriods[periodIndex - 1];
      const current = sourcePeriods[periodIndex];
      levels = computePivotPointLevels(pivotType, previous, current.open);
      periodLevelsCache.set(periodIndex, levels);
    }

    for (const level of PIVOT_POINTS_LEVELS) {
      const value = levels[level];
      if (value !== undefined && Number.isFinite(value)) {
        pivotSeries[level][index] = value;
      }
    }
  }

  return pivotSeries;
}

function buildSwingPivots(highs: number[], lows: number[], depth: number): SwingPivot[] {
  const period = clampInteger(depth, 2, 200);
  const pivots: SwingPivot[] = [];

  for (let index = period; index < highs.length - period; index += 1) {
    let isHigh = true;
    let isLow = true;

    for (let offset = 1; offset <= period; offset += 1) {
      if (highs[index] < highs[index - offset] || highs[index] < highs[index + offset]) {
        isHigh = false;
      }
      if (lows[index] > lows[index - offset] || lows[index] > lows[index + offset]) {
        isLow = false;
      }
      if (!isHigh && !isLow) {
        break;
      }
    }

    if (isHigh === isLow) {
      continue;
    }

    const pivot: SwingPivot = isHigh
      ? { index, price: highs[index], kind: 'high' }
      : { index, price: lows[index], kind: 'low' };
    const previous = pivots[pivots.length - 1];

    if (previous && previous.kind === pivot.kind) {
      const shouldReplace =
        (pivot.kind === 'high' && pivot.price >= previous.price) ||
        (pivot.kind === 'low' && pivot.price <= previous.price);
      if (shouldReplace) {
        pivots[pivots.length - 1] = pivot;
      }
      continue;
    }

    pivots.push(pivot);
  }

  return pivots;
}

function latestOppositePivotPair(pivots: SwingPivot[], uptoIndex: number): [SwingPivot, SwingPivot] | null {
  let newest: SwingPivot | null = null;

  for (let index = pivots.length - 1; index >= 0; index -= 1) {
    const pivot = pivots[index];
    if (pivot.index > uptoIndex) {
      continue;
    }

    if (!newest) {
      newest = pivot;
      continue;
    }

    if (pivot.kind !== newest.kind) {
      return [pivot, newest];
    }
  }

  return null;
}

function latestAlternatingTriplet(
  pivots: SwingPivot[],
  uptoIndex: number,
): [SwingPivot, SwingPivot, SwingPivot] | null {
  const reversed: SwingPivot[] = [];
  let lastKind: SwingPivot['kind'] | null = null;

  for (let index = pivots.length - 1; index >= 0; index -= 1) {
    const pivot = pivots[index];
    if (pivot.index > uptoIndex) {
      continue;
    }
    if (pivot.kind === lastKind) {
      continue;
    }
    reversed.push(pivot);
    lastKind = pivot.kind;
    if (reversed.length === 3) {
      break;
    }
  }

  if (reversed.length < 3) {
    return null;
  }

  return [reversed[2], reversed[1], reversed[0]];
}

function makeFibLikeSeries(
  highs: number[],
  lows: number[],
  length: number,
  mode: 'retracement' | 'extension',
): { primary: Array<number | undefined>; secondary: Array<number | undefined>; tertiary: Array<number | undefined> } {
  const pivots = buildSwingPivots(highs, lows, Math.max(2, Math.round(length / 3)));
  const primary: Array<number | undefined> = new Array(highs.length).fill(undefined);
  const secondary: Array<number | undefined> = new Array(highs.length).fill(undefined);
  const tertiary: Array<number | undefined> = new Array(highs.length).fill(undefined);

  for (let index = 0; index < highs.length; index += 1) {
    const rangePair = latestOppositePivotPair(pivots, index);
    if (!rangePair) {
      continue;
    }

    const [older, newer] = rangePair;
    const anchorIndex = Math.max(older.index, newer.index);
    if (index < anchorIndex) {
      continue;
    }

    const hi = Math.max(older.price, newer.price);
    const lo = Math.min(older.price, newer.price);
    const range = hi - lo;
    const upswing = older.kind === 'low' && newer.kind === 'high';

    if (range === 0) {
      primary[index] = lo;
      secondary[index] = lo;
      tertiary[index] = hi;
      continue;
    }

    if (mode === 'retracement') {
      if (upswing) {
        primary[index] = hi - range * 0.382;
        secondary[index] = hi - range * 0.618;
        tertiary[index] = lo;
      } else {
        primary[index] = lo + range * 0.382;
        secondary[index] = lo + range * 0.618;
        tertiary[index] = hi;
      }
    } else {
      if (upswing) {
        primary[index] = hi + range * 0.272;
        secondary[index] = hi + range * 0.618;
        tertiary[index] = hi + range;
      } else {
        primary[index] = lo - range * 0.272;
        secondary[index] = lo - range * 0.618;
        tertiary[index] = lo - range;
      }
    }
  }

  return { primary, secondary, tertiary };
}

function makePitchforkSeries(
  highs: number[],
  lows: number[],
  length: number,
): { primary: Array<number | undefined>; secondary: Array<number | undefined>; tertiary: Array<number | undefined> } {
  const pivots = buildSwingPivots(highs, lows, Math.max(2, Math.round(length / 3)));
  const primary: Array<number | undefined> = new Array(highs.length).fill(undefined);
  const secondary: Array<number | undefined> = new Array(highs.length).fill(undefined);
  const tertiary: Array<number | undefined> = new Array(highs.length).fill(undefined);

  for (let index = 0; index < highs.length; index += 1) {
    const anchors = latestAlternatingTriplet(pivots, index);
    if (!anchors) {
      continue;
    }

    const [a, b, c] = anchors;
    if (index < a.index) {
      continue;
    }

    const midpointIndex = (b.index + c.index) / 2;
    const midpointPrice = (b.price + c.price) / 2;
    const denominator = midpointIndex - a.index;
    if (denominator === 0) {
      continue;
    }

    const slope = (midpointPrice - a.price) / denominator;
    const center = a.price + slope * (index - a.index);
    const offsetB = b.price - (a.price + slope * (b.index - a.index));
    const offsetC = c.price - (a.price + slope * (c.index - a.index));
    const upper = center + offsetB;
    const lower = center + offsetC;

    primary[index] = center;
    secondary[index] = Math.max(upper, lower);
    tertiary[index] = Math.min(upper, lower);
  }

  return { primary, secondary, tertiary };
}

function makeRobBookerGhostPivots(
  highs: number[],
  lows: number[],
  length: number,
): { primary: Array<number | undefined>; secondary: Array<number | undefined>; tertiary: Array<number | undefined> } {
  const pivots = buildSwingPivots(highs, lows, Math.max(2, Math.round(length / 3)));
  const primary: Array<number | undefined> = new Array(highs.length).fill(undefined);
  const secondary: Array<number | undefined> = new Array(highs.length).fill(undefined);
  const tertiary: Array<number | undefined> = new Array(highs.length).fill(undefined);

  for (let index = 0; index < highs.length; index += 1) {
    const rangePair = latestOppositePivotPair(pivots, index);
    if (!rangePair) {
      continue;
    }

    const [older, newer] = rangePair;
    const anchorIndex = Math.max(older.index, newer.index);
    if (index < anchorIndex) {
      continue;
    }

    const highLevel = Math.max(older.price, newer.price);
    const lowLevel = Math.min(older.price, newer.price);
    primary[index] = (highLevel + lowLevel) / 2;
    secondary[index] = highLevel;
    tertiary[index] = lowLevel;
  }

  return { primary, secondary, tertiary };
}

function makeBucketCloseSeries(closes: number[], bucketSize: number): Array<number | undefined> {
  const size = clampInteger(bucketSize, 1, 500);
  const output: Array<number | undefined> = new Array(closes.length).fill(undefined);

  for (let start = 0; start < closes.length; start += size) {
    const end = Math.min(closes.length, start + size) - 1;
    const value = closes[end];
    for (let index = start; index <= end; index += 1) {
      output[index] = value;
    }
  }

  return output;
}

function makeZigZagApprox(
  highs: number[],
  lows: number[],
  closes: number[],
  length: number,
): Array<number | undefined> {
  const pivots = buildSwingPivots(highs, lows, Math.max(2, Math.round(length / 4)));
  const points: SwingPivot[] = [...pivots];
  const lastIndex = closes.length - 1;
  const output: Array<number | undefined> = new Array(closes.length).fill(undefined);

  if (!closes.length) {
    return output;
  }

  if (!points.length) {
    points.push({ index: 0, price: closes[0], kind: 'low' });
    points.push({ index: lastIndex, price: closes[lastIndex], kind: 'high' });
  } else {
    const first = points[0];
    if (first.index !== 0) {
      points.unshift({ index: 0, price: closes[0], kind: first.kind === 'high' ? 'low' : 'high' });
    }
    const last = points[points.length - 1];
    if (last.index !== lastIndex) {
      points.push({
        index: lastIndex,
        price: closes[lastIndex],
        kind: last.kind === 'high' ? 'low' : 'high',
      });
    }
  }

  for (let pivotIndex = 0; pivotIndex < points.length - 1; pivotIndex += 1) {
    const start = points[pivotIndex];
    const end = points[pivotIndex + 1];
    const span = Math.max(1, end.index - start.index);

    for (let index = start.index; index <= end.index; index += 1) {
      const ratio = (index - start.index) / span;
      output[index] = start.price + (end.price - start.price) * ratio;
    }
  }

  return output;
}

function visibleRangeIndices(
  length: number,
  context: BasicStudyContext | undefined,
): { from: number; to: number } | null {
  if (!context?.visibleRange) {
    return null;
  }

  const from = Math.max(0, Math.min(length - 1, Math.floor(context.visibleRange.fromIndex)));
  const to = Math.max(0, Math.min(length - 1, Math.ceil(context.visibleRange.toIndex)));
  if (from > to) {
    return null;
  }

  return { from, to };
}

function visibleAverageSeries(
  candles: Candle[],
  typical: number[],
  fallbackLength: number,
  context: BasicStudyContext | undefined,
): Array<number | undefined> {
  const output: Array<number | undefined> = new Array(candles.length).fill(undefined);
  if (!candles.length) {
    return output;
  }

  const visible = visibleRangeIndices(candles.length, context);
  const rangeFrom = visible ? visible.from : Math.max(0, candles.length - fallbackLength);
  const rangeTo = visible ? visible.to : candles.length - 1;

  let weightedPrice = 0;
  let totalWeight = 0;
  for (let index = rangeFrom; index <= rangeTo; index += 1) {
    const weight = Math.max(1, candles[index].volume);
    weightedPrice += typical[index] * weight;
    totalWeight += weight;
  }

  const average = safeDivide(weightedPrice, totalWeight);
  for (let index = 0; index < candles.length; index += 1) {
    output[index] = average;
  }

  return output;
}

function sessionAverageSeries(
  candles: Candle[],
  typical: number[],
  weighted: boolean,
): Array<number | undefined> {
  let cumulativePrice = 0;
  let cumulativeWeight = 0;
  let currentDay = '';

  return candles.map((candle, index) => {
    const day = utcDayKey(candle);
    if (day !== currentDay) {
      currentDay = day;
      cumulativePrice = 0;
      cumulativeWeight = 0;
    }

    const weight = weighted ? Math.max(1, candle.volume) : 1;
    cumulativePrice += typical[index] * weight;
    cumulativeWeight += weight;
    return safeDivide(cumulativePrice, cumulativeWeight);
  });
}

function seasonalitySeries(
  candles: Candle[],
  smoothingLength: number,
): { primary: Array<number | undefined>; secondary: Array<number | undefined> } {
  const daySums = new Map<number, { sum: number; count: number }>();

  candles.forEach((candle) => {
    const day = utcDayOfYear(candle);
    const bucket = daySums.get(day) ?? { sum: 0, count: 0 };
    bucket.sum += candle.close;
    bucket.count += 1;
    daySums.set(day, bucket);
  });

  const primary = candles.map((candle) => {
    const bucket = daySums.get(utcDayOfYear(candle));
    if (!bucket || bucket.count === 0) {
      return undefined;
    }
    return bucket.sum / bucket.count;
  });
  const secondary = emaMaybe(primary, Math.max(2, smoothingLength));

  return { primary, secondary };
}

function emptyOutput(length: number): BasicStudyComputation {
  return {
    primary: new Array(length).fill(undefined),
    secondary: new Array(length).fill(undefined),
    tertiary: new Array(length).fill(undefined),
    histogram: new Array(length).fill(undefined),
    pivotLevels: undefined,
  };
}

export function createDefaultBasicStudySettingsMap(): Record<BasicIndicatorKeyRaw, BasicStudySettings> {
  const base = {
    style: {
      primaryColor: '#38bdf8',
      secondaryColor: '#f59e0b',
      tertiaryColor: '#f97316',
      positiveColor: '#22c55e',
      negativeColor: '#ef4444',
      lineWidth: 2 as const,
      lineStyle: 'solid' as const,
    },
    visibility: {
      showPrimary: true,
      showSecondary: true,
      showTertiary: true,
      showHistogram: true,
      showSeriesLabels: true,
    },
  };

  const createSettings = (inputs: BasicStudySettings['inputs']): BasicStudySettings => ({
    inputs,
    style: { ...base.style },
    visibility: { ...base.visibility },
  });

  const createStyledSettings = (
    inputs: BasicStudySettings['inputs'],
    stylePatch: Partial<BasicStudySettings['style']>,
    visibilityPatch?: Partial<BasicStudySettings['visibility']>,
  ): BasicStudySettings => {
    const defaults = createSettings(inputs);
    return {
      ...defaults,
      style: {
        ...defaults.style,
        ...stylePatch,
      },
      visibility: {
        ...defaults.visibility,
        ...(visibilityPatch ?? {}),
      },
    };
  };

  const pivotPointsStandardBase = createSettings({ length: 50, length2: 20, length3: 10, length4: 5, multiplier: 2 });

  const explicit: Partial<Record<BasicIndicatorKeyRaw, BasicStudySettings>> = {
    ema: createSettings({ length: 20, length2: 9, length3: 5, length4: 3, multiplier: 2 }),
    wma: createSettings({ length: 20, length2: 9, length3: 5, length4: 3, multiplier: 2 }),
    hma: createSettings({ length: 20, length2: 9, length3: 5, length4: 3, multiplier: 2 }),
    dema: createSettings({ length: 20, length2: 9, length3: 5, length4: 3, multiplier: 2 }),
    tema: createSettings({ length: 20, length2: 9, length3: 5, length4: 3, multiplier: 2 }),
    vwma: createSettings({ length: 20, length2: 9, length3: 5, length4: 3, multiplier: 2 }),
    vwap: createSettings({ length: 1, length2: 1, length3: 1, length4: 1, multiplier: 2 }),
    supertrend: createSettings({ length: 10, length2: 10, length3: 10, length4: 10, multiplier: 3 }),
    parabolicSar: createSettings({ length: 2, length2: 20, length3: 2, length4: 2, multiplier: 2 }),
    donchianChannels: createSettings({ length: 20, length2: 20, length3: 20, length4: 20, multiplier: 2 }),
    stochastic: createSettings({ length: 14, length2: 3, length3: 3, length4: 3, multiplier: 2 }),
    stochasticRsi: createSettings({ length: 14, length2: 14, length3: 3, length4: 3, multiplier: 2 }),
    cci: createSettings({ length: 20, length2: 20, length3: 20, length4: 20, multiplier: 2 }),
    williamsR: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    roc: createSettings({ length: 12, length2: 12, length3: 12, length4: 12, multiplier: 2 }),
    ultimateOscillator: createSettings({ length: 7, length2: 14, length3: 28, length4: 28, multiplier: 2 }),
    awesomeOscillator: {
      ...createSettings({ length: 5, length2: 34, length3: 34, length4: 34, multiplier: 2 }),
      visibility: {
        ...base.visibility,
        showPrimary: false,
      },
    },
    aroon: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    aroonOscillator: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    trendStrengthIndex: createSettings({ length: 25, length2: 13, length3: 7, length4: 7, multiplier: 2 }),
    maCross: createSettings({
      length: 9,
      length2: 21,
      length3: 5,
      length4: 3,
      multiplier: 2,
      maCrossLine1Type: DEFAULT_MA_CROSS_LINE_1_TYPE,
      maCrossLine2Type: DEFAULT_MA_CROSS_LINE_2_TYPE,
      maCrossLine1OffsetPercent: DEFAULT_MA_CROSS_LINE_1_OFFSET_PERCENT,
      maCrossLine2OffsetPercent: DEFAULT_MA_CROSS_LINE_2_OFFSET_PERCENT,
    }),
    autoFibExtension: createStyledSettings(
      { length: 50, length2: 20, length3: 10, length4: 5, multiplier: 2 },
      {
        primaryColor: '#0ea5e9',
        secondaryColor: '#f59e0b',
        tertiaryColor: '#ef4444',
        lineStyle: 'dashed',
      },
    ),
    autoFibRetracement: createStyledSettings(
      { length: 50, length2: 20, length3: 10, length4: 5, multiplier: 2 },
      {
        primaryColor: '#2563eb',
        secondaryColor: '#16a34a',
        tertiaryColor: '#64748b',
        lineStyle: 'dashed',
      },
    ),
    autoPitchfork: createStyledSettings(
      { length: 50, length2: 20, length3: 10, length4: 5, multiplier: 2 },
      {
        primaryColor: '#2563eb',
        secondaryColor: '#0ea5e9',
        tertiaryColor: '#0ea5e9',
        lineStyle: 'solid',
      },
    ),
    atr: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    adx: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    dmi: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    mfi: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    obv: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    chaikinMoneyFlow: createSettings({ length: 20, length2: 20, length3: 20, length4: 20, multiplier: 2 }),
    chartPatterns: createStyledSettings(
      { length: 3, length2: 180, length3: 8, length4: 1, multiplier: 2 },
      {
        primaryColor: '#22c55e',
        secondaryColor: '#ef4444',
        tertiaryColor: '#f59e0b',
        lineWidth: 2,
        lineStyle: 'solid',
      },
    ),
    cumulativeVolumeDelta: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    robBookerZivGhostPivots: createStyledSettings(
      { length: 50, length2: 20, length3: 10, length4: 5, multiplier: 2 },
      {
        primaryColor: '#f59e0b',
        secondaryColor: '#ef4444',
        tertiaryColor: '#22c55e',
        lineStyle: 'dotted',
      },
    ),
    pivotPointsStandard: {
      ...pivotPointsStandardBase,
      inputs: {
        ...pivotPointsStandardBase.inputs,
        pivotPointsType: DEFAULT_PIVOT_POINTS_TYPE,
        pivotPointsTimeframe: DEFAULT_PIVOT_POINTS_TIMEFRAME,
        pivotPointsBack: DEFAULT_PIVOT_POINTS_BACK,
        pivotPointsUseDailyBasedValues: DEFAULT_PIVOT_POINTS_USE_DAILY_BASED_VALUES,
        pivotPointsShowLabels: DEFAULT_PIVOT_POINTS_SHOW_LABELS,
        pivotPointsShowPrices: DEFAULT_PIVOT_POINTS_SHOW_PRICES,
        pivotPointsLabelsPosition: DEFAULT_PIVOT_POINTS_LABELS_POSITION,
      },
      style: {
        ...pivotPointsStandardBase.style,
        pivotLevelsColor: { ...DEFAULT_PIVOT_LEVELS_COLOR },
      },
      visibility: {
        ...pivotPointsStandardBase.visibility,
        pivotLevels: { ...DEFAULT_PIVOT_LEVELS_VISIBILITY },
      },
    },
    priceVolumeTrend: createSettings({ length: 14, length2: 14, length3: 14, length4: 14, multiplier: 2 }),
    bollingerBandwidth: createSettings({ length: 20, length2: 20, length3: 20, length4: 20, multiplier: 2 }),
    bollingerPercentB: createSettings({ length: 20, length2: 20, length3: 20, length4: 20, multiplier: 2 }),
    zigZag: createStyledSettings(
      { length: 50, length2: 20, length3: 10, length4: 5, multiplier: 2 },
      {
        primaryColor: '#f97316',
        lineWidth: 3,
        lineStyle: 'solid',
      },
      {
        showSecondary: false,
        showTertiary: false,
      },
    ),
  };

  const map = Object.fromEntries(
    BASIC_INDICATOR_KEYS.map((key) => {
      const existing = explicit[key];
      if (existing) {
        return [key, existing] as const;
      }

      if (key === 'volume24h') {
        return [key, createSettings({ length: 24, length2: 12, length3: 6, length4: 3, multiplier: 2 })] as const;
      }

      if (key.startsWith('auto') || key.includes('pivot') || key === 'tradingSessions') {
        return [key, createSettings({ length: 50, length2: 20, length3: 10, length4: 5, multiplier: 2 })] as const;
      }

      if (BASIC_INDICATOR_DEFINITIONS[key].series.histogram) {
        return [key, createSettings({ length: 20, length2: 9, length3: 6, length4: 3, multiplier: 2 })] as const;
      }

      if (BASIC_INDICATOR_DEFINITIONS[key].pane) {
        return [key, createSettings({ length: 14, length2: 7, length3: 5, length4: 3, multiplier: 2 })] as const;
      }

      return [key, createSettings({ length: 20, length2: 10, length3: 5, length4: 3, multiplier: 2 })] as const;
    }),
  );

  return map as Record<BasicIndicatorKeyRaw, BasicStudySettings>;
}

export function computeBasicStudy(
  key: BasicIndicatorKeyRaw,
  candles: Candle[],
  settings: BasicStudySettings,
  context?: BasicStudyContext,
): BasicStudyComputation {
  const output = emptyOutput(candles.length);
  if (!candles.length) {
    return output;
  }

  if (key === 'chartPatterns') {
    return output;
  }

  const closes = closeValues(candles);
  const highs = candles.map((candle) => candle.high);
  const lows = candles.map((candle) => candle.low);
  const volumes = candles.map((candle) => candle.volume);
  const hl2 = hl2Values(candles);
  const length = clampInteger(settings.inputs.length, 1, 500);
  const length2 = clampInteger(settings.inputs.length2, 1, 500);
  const length3 = clampInteger(settings.inputs.length3, 1, 500);
  const length4 = clampInteger(settings.inputs.length4, 1, 500);
  const multiplier = Math.max(0.1, Math.min(10, settings.inputs.multiplier));

  if (key === 'ema') {
    output.primary = ema(closes, length);
    return output;
  }

  if (key === 'wma') {
    output.primary = wma(closes, length);
    return output;
  }

  if (key === 'hma') {
    const half = Math.max(1, Math.round(length / 2));
    const sqrtLen = Math.max(1, Math.round(Math.sqrt(length)));
    const wmaHalf = wma(closes, half);
    const wmaFull = wma(closes, length);
    const diff: number[] = closes.map((_, index) => {
      const left = wmaHalf[index] ?? 0;
      const right = wmaFull[index] ?? 0;
      return 2 * left - right;
    });
    output.primary = wma(diff, sqrtLen);
    return output;
  }

  if (key === 'dema') {
    const ema1 = ema(closes, length);
    const ema2 = emaMaybe(ema1, length);
    output.primary = closes.map((_, index) => {
      const first = ema1[index];
      const second = ema2[index];
      if (first === undefined || second === undefined) {
        return undefined;
      }
      return 2 * first - second;
    });
    return output;
  }

  if (key === 'tema') {
    const ema1 = ema(closes, length);
    const ema2 = emaMaybe(ema1, length);
    const ema3 = emaMaybe(ema2, length);
    output.primary = closes.map((_, index) => {
      const first = ema1[index];
      const second = ema2[index];
      const third = ema3[index];
      if (first === undefined || second === undefined || third === undefined) {
        return undefined;
      }
      return 3 * first - 3 * second + third;
    });
    return output;
  }

  if (key === 'vwma') {
    output.primary = vwma(closes, volumes, length);
    return output;
  }

  if (key === 'maCross') {
    const line1TypeInput = settings.inputs.maCrossLine1Type;
    const line2TypeInput = settings.inputs.maCrossLine2Type;
    const line1OffsetPercentInput = settings.inputs.maCrossLine1OffsetPercent;
    const line2OffsetPercentInput = settings.inputs.maCrossLine2OffsetPercent;
    const line1Type: BasicStudyMaCrossLineType =
      line1TypeInput === 'ma' || line1TypeInput === 'sma' || line1TypeInput === 'ema'
        ? line1TypeInput
        : DEFAULT_MA_CROSS_LINE_1_TYPE;
    const line2Type: BasicStudyMaCrossLineType =
      line2TypeInput === 'ma' || line2TypeInput === 'sma' || line2TypeInput === 'ema'
        ? line2TypeInput
        : DEFAULT_MA_CROSS_LINE_2_TYPE;
    const line1OffsetPercent =
      typeof line1OffsetPercentInput === 'number' && Number.isFinite(line1OffsetPercentInput)
        ? line1OffsetPercentInput
        : DEFAULT_MA_CROSS_LINE_1_OFFSET_PERCENT;
    const line2OffsetPercent =
      typeof line2OffsetPercentInput === 'number' && Number.isFinite(line2OffsetPercentInput)
        ? line2OffsetPercentInput
        : DEFAULT_MA_CROSS_LINE_2_OFFSET_PERCENT;

    output.primary = applyOffsetPercent(maCrossMovingAverageByType(closes, length, line1Type), line1OffsetPercent);
    output.secondary = applyOffsetPercent(maCrossMovingAverageByType(closes, length2, line2Type), line2OffsetPercent);
    return output;
  }

  if (key === 'vwap') {
    let cumulativePv = 0;
    let cumulativeVol = 0;
    output.primary = candles.map((candle) => {
      const typical = (candle.high + candle.low + candle.close) / 3;
      cumulativePv += typical * candle.volume;
      cumulativeVol += candle.volume;
      if (cumulativeVol === 0) {
        return undefined;
      }
      return cumulativePv / cumulativeVol;
    });
    return output;
  }

  if (key === 'supertrend') {
    const atrValues = rma(trueRange(candles), length);
    const upperBand: Array<number | undefined> = new Array(candles.length).fill(undefined);
    const lowerBand: Array<number | undefined> = new Array(candles.length).fill(undefined);
    const trendDirection: boolean[] = new Array(candles.length).fill(true);

    for (let index = 0; index < candles.length; index += 1) {
      const atr = atrValues[index];
      if (atr === undefined) {
        continue;
      }

      const hl = (candles[index].high + candles[index].low) / 2;
      const basicUpper = hl + multiplier * atr;
      const basicLower = hl - multiplier * atr;

      if (index === 0) {
        upperBand[index] = basicUpper;
        lowerBand[index] = basicLower;
        trendDirection[index] = true;
        output.primary[index] = basicLower;
        continue;
      }

      const previousUpper = upperBand[index - 1] ?? basicUpper;
      const previousLower = lowerBand[index - 1] ?? basicLower;
      const previousClose = candles[index - 1].close;

      upperBand[index] =
        basicUpper < previousUpper || previousClose > previousUpper ? basicUpper : previousUpper;
      lowerBand[index] =
        basicLower > previousLower || previousClose < previousLower ? basicLower : previousLower;

      const previousTrend = trendDirection[index - 1];
      const currentClose = candles[index].close;
      if (currentClose > (upperBand[index - 1] ?? basicUpper)) {
        trendDirection[index] = true;
      } else if (currentClose < (lowerBand[index - 1] ?? basicLower)) {
        trendDirection[index] = false;
      } else {
        trendDirection[index] = previousTrend;
      }

      output.primary[index] = trendDirection[index]
        ? (lowerBand[index] ?? undefined)
        : (upperBand[index] ?? undefined);
    }

    return output;
  }

  if (key === 'parabolicSar') {
    output.primary[0] = candles[0].low;
    if (candles.length < 2) {
      return output;
    }

    let isUpTrend = candles[1].close >= candles[0].close;
    let afStep = Math.max(0.01, Math.min(0.2, length / 100));
    if (!Number.isFinite(afStep)) {
      afStep = 0.02;
    }
    const afMax = Math.max(afStep, Math.min(0.6, settings.inputs.length2 / 100));
    let af = afStep;
    let ep = isUpTrend ? candles[0].high : candles[0].low;
    let sar = isUpTrend ? candles[0].low : candles[0].high;

    for (let index = 1; index < candles.length; index += 1) {
      sar = sar + af * (ep - sar);

      const previous = candles[index - 1];
      const current = candles[index];

      if (isUpTrend) {
        sar = Math.min(sar, previous.low, current.low);
        if (current.low < sar) {
          isUpTrend = false;
          sar = ep;
          ep = current.low;
          af = afStep;
        } else if (current.high > ep) {
          ep = current.high;
          af = Math.min(af + afStep, afMax);
        }
      } else {
        sar = Math.max(sar, previous.high, current.high);
        if (current.high > sar) {
          isUpTrend = true;
          sar = ep;
          ep = current.high;
          af = afStep;
        } else if (current.low < ep) {
          ep = current.low;
          af = Math.min(af + afStep, afMax);
        }
      }

      output.primary[index] = sar;
    }

    return output;
  }

  if (key === 'donchianChannels') {
    const upper = highest(highs, length);
    const lower = lowest(lows, length);
    output.primary = upper;
    output.tertiary = lower;
    output.secondary = upper.map((value, index) => {
      const low = lower[index];
      if (value === undefined || low === undefined) {
        return undefined;
      }
      return (value + low) / 2;
    });
    return output;
  }

  if (key === 'stochastic') {
    const highestHigh = highest(highs, length);
    const lowestLow = lowest(lows, length);
    const kRaw = closes.map((close, index) => {
      const high = highestHigh[index];
      const low = lowestLow[index];
      if (high === undefined || low === undefined || high === low) {
        return undefined;
      }
      return ((close - low) / (high - low)) * 100;
    });
    output.primary = sma(
      kRaw.map((value) => value ?? 0),
      length2,
    ).map((value, index) => (kRaw[index] === undefined ? undefined : value));
    output.secondary = sma(
      output.primary.map((value) => value ?? 0),
      length3,
    ).map((value, index) => (output.primary[index] === undefined ? undefined : value));
    return output;
  }

  if (key === 'stochasticRsi') {
    const rsi = computeRsi(closes, length);
    const rsiValues = rsi.map((value) => value ?? 0);
    const rsiHighest = highest(rsiValues, length2);
    const rsiLowest = lowest(rsiValues, length2);
    const stochRsi = rsi.map((value, index) => {
      const high = rsiHighest[index];
      const low = rsiLowest[index];
      if (value === undefined || high === undefined || low === undefined || high === low) {
        return undefined;
      }
      return ((value - low) / (high - low)) * 100;
    });

    output.primary = sma(
      stochRsi.map((value) => value ?? 0),
      length3,
    ).map((value, index) => (stochRsi[index] === undefined ? undefined : value));
    output.secondary = sma(
      output.primary.map((value) => value ?? 0),
      length4,
    ).map((value, index) => (output.primary[index] === undefined ? undefined : value));
    return output;
  }

  if (key === 'cci') {
    const typical = candles.map((candle) => (candle.high + candle.low + candle.close) / 3);
    const basis = sma(typical, length);
    output.primary = typical.map((value, index) => {
      const average = basis[index];
      if (average === undefined) {
        return undefined;
      }

      let deviation = 0;
      for (let offset = 0; offset < length; offset += 1) {
        deviation += Math.abs(typical[index - offset] - average);
      }
      const meanDeviation = deviation / length;
      if (meanDeviation === 0) {
        return 0;
      }

      return (value - average) / (0.015 * meanDeviation);
    });
    return output;
  }

  if (key === 'williamsR') {
    const highestHigh = highest(highs, length);
    const lowestLow = lowest(lows, length);
    output.primary = closes.map((close, index) => {
      const high = highestHigh[index];
      const low = lowestLow[index];
      if (high === undefined || low === undefined || high === low) {
        return undefined;
      }
      return ((high - close) / (high - low)) * -100;
    });
    return output;
  }

  if (key === 'roc') {
    output.primary = closes.map((close, index) => {
      if (index < length) {
        return undefined;
      }
      const previous = closes[index - length];
      if (previous === 0) {
        return undefined;
      }
      return ((close - previous) / previous) * 100;
    });
    return output;
  }

  if (key === 'ultimateOscillator') {
    const buyingPressure: number[] = new Array(candles.length).fill(0);
    const tr: number[] = new Array(candles.length).fill(0);

    for (let index = 0; index < candles.length; index += 1) {
      const previousClose = index === 0 ? candles[index].close : candles[index - 1].close;
      const minLow = Math.min(candles[index].low, previousClose);
      const maxHigh = Math.max(candles[index].high, previousClose);
      buyingPressure[index] = candles[index].close - minLow;
      tr[index] = maxHigh - minLow;
    }

    const bp1 = rollingSum(buyingPressure, length);
    const bp2 = rollingSum(buyingPressure, length2);
    const bp3 = rollingSum(buyingPressure, length3);
    const tr1 = rollingSum(tr, length);
    const tr2 = rollingSum(tr, length2);
    const tr3 = rollingSum(tr, length3);

    output.primary = closes.map((_, index) => {
      const numerator1 = bp1[index];
      const numerator2 = bp2[index];
      const numerator3 = bp3[index];
      const denominator1 = tr1[index];
      const denominator2 = tr2[index];
      const denominator3 = tr3[index];

      if (
        numerator1 === undefined ||
        numerator2 === undefined ||
        numerator3 === undefined ||
        denominator1 === undefined ||
        denominator2 === undefined ||
        denominator3 === undefined ||
        denominator1 === 0 ||
        denominator2 === 0 ||
        denominator3 === 0
      ) {
        return undefined;
      }

      const avg1 = numerator1 / denominator1;
      const avg2 = numerator2 / denominator2;
      const avg3 = numerator3 / denominator3;
      return ((4 * avg1 + 2 * avg2 + avg3) / 7) * 100;
    });
    return output;
  }

  if (key === 'awesomeOscillator') {
    const fast = sma(hl2, length);
    const slow = sma(hl2, length2);
    output.histogram = hl2.map((_, index) => {
      const fastValue = fast[index];
      const slowValue = slow[index];
      if (fastValue === undefined || slowValue === undefined) {
        return undefined;
      }
      return fastValue - slowValue;
    });
    return output;
  }

  if (key === 'aroon' || key === 'aroonOscillator') {
    const up: Array<number | undefined> = new Array(candles.length).fill(undefined);
    const down: Array<number | undefined> = new Array(candles.length).fill(undefined);

    for (let index = length - 1; index < candles.length; index += 1) {
      let highestIndex = index;
      let lowestIndex = index;
      for (let offset = 1; offset < length; offset += 1) {
        const candidateIndex = index - offset;
        if (highs[candidateIndex] >= highs[highestIndex]) {
          highestIndex = candidateIndex;
        }
        if (lows[candidateIndex] <= lows[lowestIndex]) {
          lowestIndex = candidateIndex;
        }
      }

      up[index] = ((length - (index - highestIndex)) / length) * 100;
      down[index] = ((length - (index - lowestIndex)) / length) * 100;
    }

    if (key === 'aroon') {
      output.primary = up;
      output.secondary = down;
    } else {
      output.primary = up.map((value, index) => {
        const other = down[index];
        if (value === undefined || other === undefined) {
          return undefined;
        }
        return value - other;
      });
    }

    return output;
  }

  if (key === 'trendStrengthIndex') {
    const momentum: Array<number | undefined> = new Array(candles.length).fill(undefined);
    const absoluteMomentum: Array<number | undefined> = new Array(candles.length).fill(undefined);
    for (let index = 1; index < closes.length; index += 1) {
      const delta = closes[index] - closes[index - 1];
      momentum[index] = delta;
      absoluteMomentum[index] = Math.abs(delta);
    }

    const emaMomentum = emaMaybe(momentum, length);
    const emaAbsMomentum = emaMaybe(absoluteMomentum, length);
    const emaMomentum2 = emaMaybe(emaMomentum, length2);
    const emaAbsMomentum2 = emaMaybe(emaAbsMomentum, length2);

    output.primary = closes.map((_, index) => {
      const top = emaMomentum2[index];
      const bottom = emaAbsMomentum2[index];
      if (top === undefined || bottom === undefined || bottom === 0) {
        return undefined;
      }
      return (top / bottom) * 100;
    });
    output.secondary = emaMaybe(output.primary, length3);
    return output;
  }

  if (key === 'atr') {
    output.primary = rma(trueRange(candles), length);
    return output;
  }

  if (key === 'adx' || key === 'dmi') {
    const tr = trueRange(candles);
    const plusDm: number[] = new Array(candles.length).fill(0);
    const minusDm: number[] = new Array(candles.length).fill(0);

    for (let index = 1; index < candles.length; index += 1) {
      const upMove = candles[index].high - candles[index - 1].high;
      const downMove = candles[index - 1].low - candles[index].low;

      plusDm[index] = upMove > downMove && upMove > 0 ? upMove : 0;
      minusDm[index] = downMove > upMove && downMove > 0 ? downMove : 0;
    }

    const smoothedTr = rma(tr, length);
    const smoothedPlusDm = rma(plusDm, length);
    const smoothedMinusDm = rma(minusDm, length);

    const plusDi = closes.map((_, index) => {
      const denominator = smoothedTr[index];
      const numerator = smoothedPlusDm[index];
      if (denominator === undefined || numerator === undefined || denominator === 0) {
        return undefined;
      }
      return (numerator / denominator) * 100;
    });

    const minusDi = closes.map((_, index) => {
      const denominator = smoothedTr[index];
      const numerator = smoothedMinusDm[index];
      if (denominator === undefined || numerator === undefined || denominator === 0) {
        return undefined;
      }
      return (numerator / denominator) * 100;
    });

    const dx = closes.map((_, index) => {
      const plus = plusDi[index];
      const minus = minusDi[index];
      if (plus === undefined || minus === undefined || plus + minus === 0) {
        return 0;
      }
      return (Math.abs(plus - minus) / (plus + minus)) * 100;
    });

    const adx = rma(dx, length2);

    if (key === 'adx') {
      output.primary = adx;
    } else {
      output.primary = plusDi;
      output.secondary = minusDi;
      output.tertiary = adx;
    }
    return output;
  }

  if (key === 'mfi') {
    const typical = candles.map((candle) => (candle.high + candle.low + candle.close) / 3);
    const positiveFlow: number[] = new Array(candles.length).fill(0);
    const negativeFlow: number[] = new Array(candles.length).fill(0);

    for (let index = 1; index < candles.length; index += 1) {
      const flow = typical[index] * candles[index].volume;
      if (typical[index] > typical[index - 1]) {
        positiveFlow[index] = flow;
      } else if (typical[index] < typical[index - 1]) {
        negativeFlow[index] = flow;
      }
    }

    const positive = rollingSum(positiveFlow, length);
    const negative = rollingSum(negativeFlow, length);

    output.primary = closes.map((_, index) => {
      const pos = positive[index];
      const neg = negative[index];
      if (pos === undefined || neg === undefined) {
        return undefined;
      }
      if (neg === 0) {
        return 100;
      }
      const ratio = pos / neg;
      return 100 - 100 / (1 + ratio);
    });
    return output;
  }

  if (key === 'obv') {
    let cumulative = 0;
    output.primary = candles.map((candle, index) => {
      if (index === 0) {
        cumulative = candle.volume;
        return cumulative;
      }

      const previousClose = candles[index - 1].close;
      if (candle.close > previousClose) {
        cumulative += candle.volume;
      } else if (candle.close < previousClose) {
        cumulative -= candle.volume;
      }
      return cumulative;
    });
    return output;
  }

  if (key === 'chaikinMoneyFlow') {
    const moneyFlowVolume = candles.map((candle) => {
      const range = candle.high - candle.low;
      if (range === 0) {
        return 0;
      }
      const multiplierValue = ((candle.close - candle.low) - (candle.high - candle.close)) / range;
      return multiplierValue * candle.volume;
    });

    const sumFlow = rollingSum(moneyFlowVolume, length);
    const sumVol = rollingSum(volumes, length);

    output.primary = closes.map((_, index) => {
      const flow = sumFlow[index];
      const volume = sumVol[index];
      if (flow === undefined || volume === undefined || volume === 0) {
        return undefined;
      }
      return flow / volume;
    });
    return output;
  }

  if (key === 'cumulativeVolumeDelta') {
    let cumulative = 0;
    output.primary = candles.map((candle) => {
      const range = candle.high - candle.low;
      if (range === 0) {
        return cumulative;
      }
      const directionalPressure = (candle.close - candle.open) / range;
      cumulative += directionalPressure * candle.volume;
      return cumulative;
    });
    return output;
  }

  if (key === 'priceVolumeTrend') {
    let cumulative = 0;
    output.primary = candles.map((candle, index) => {
      if (index === 0) {
        return 0;
      }
      const previousClose = candles[index - 1].close;
      if (previousClose === 0) {
        return cumulative;
      }
      cumulative += ((candle.close - previousClose) / previousClose) * candle.volume;
      return cumulative;
    });
    return output;
  }

  if (key === 'bollingerBandwidth' || key === 'bollingerPercentB') {
    const basis = sma(closes, length);
    const dev = stddev(closes, length);
    const upper = basis.map((value, index) =>
      value === undefined || dev[index] === undefined ? undefined : value + multiplier * (dev[index] ?? 0),
    );
    const lower = basis.map((value, index) =>
      value === undefined || dev[index] === undefined ? undefined : value - multiplier * (dev[index] ?? 0),
    );

    if (key === 'bollingerBandwidth') {
      output.primary = closes.map((_, index) => {
        const upperValue = upper[index];
        const lowerValue = lower[index];
        const baseValue = basis[index];
        if (
          upperValue === undefined ||
          lowerValue === undefined ||
          baseValue === undefined ||
          baseValue === 0
        ) {
          return undefined;
        }
        return ((upperValue - lowerValue) / baseValue) * 100;
      });
      return output;
    }

    output.primary = closes.map((close, index) => {
      const upperValue = upper[index];
      const lowerValue = lower[index];
      if (upperValue === undefined || lowerValue === undefined || upperValue === lowerValue) {
        return undefined;
      }
      return (close - lowerValue) / (upperValue - lowerValue);
    });
    return output;
  }

  if (key === 'advanceDeclineLine') {
    const breadthByTimestamp = context?.breadthByTimestamp;
    if (breadthByTimestamp) {
      let cumulative = 0;
      output.primary = candles.map((candle) => {
        const timestampKey = candleTimestampKey(candle);
        if (!timestampKey) {
          return undefined;
        }
        const breadth = breadthByTimestamp[timestampKey];
        if (!breadth) {
          return undefined;
        }
        cumulative += breadth.advances - breadth.declines;
        return cumulative;
      });
      return output;
    }

    let breadth = 0;
    output.primary = candles.map((candle, index) => {
      if (index === 0) {
        return 0;
      }
      const previous = candles[index - 1].close;
      if (candle.close > previous) {
        breadth += 1;
      } else if (candle.close < previous) {
        breadth -= 1;
      }
      return breadth;
    });
    return output;
  }

  if (key === 'advanceDeclineRatio' || key === 'advanceDeclineRatioBars') {
    const breadthByTimestamp = context?.breadthByTimestamp;
    if (breadthByTimestamp) {
      output.primary = candles.map((candle) => {
        const timestampKey = candleTimestampKey(candle);
        if (!timestampKey) {
          return undefined;
        }
        const breadth = breadthByTimestamp[timestampKey];
        if (!breadth || breadth.declines === 0) {
          return undefined;
        }
        return breadth.advances / breadth.declines;
      });

      if (key === 'advanceDeclineRatioBars') {
        output.histogram = output.primary.map((value) => (value === undefined ? undefined : value - 1));
      }
      return output;
    }

    const advances = candles.map((candle, index) => {
      if (index === 0) {
        return 0;
      }
      return candle.close > candles[index - 1].close ? 1 : 0;
    });
    const declines = candles.map((candle, index) => {
      if (index === 0) {
        return 0;
      }
      return candle.close < candles[index - 1].close ? 1 : 0;
    });
    const rollingAdvances = rollingSum(advances, length);
    const rollingDeclines = rollingSum(declines, length);
    output.primary = closes.map((_, index) => {
      const adv = rollingAdvances[index];
      const dec = rollingDeclines[index];
      if (adv === undefined || dec === undefined || dec === 0) {
        return undefined;
      }
      return adv / dec;
    });

    if (key === 'advanceDeclineRatioBars') {
      output.histogram = output.primary.map((value) => (value === undefined ? undefined : value - 1));
    }

    return output;
  }

  if (key === 'cumulativeVolumeIndex') {
    const breadthByTimestamp = context?.breadthByTimestamp;
    if (breadthByTimestamp) {
      let cumulative = 0;
      output.primary = candles.map((candle) => {
        const timestampKey = candleTimestampKey(candle);
        if (!timestampKey) {
          return undefined;
        }
        const breadth = breadthByTimestamp[timestampKey];
        if (!breadth) {
          return undefined;
        }
        cumulative += breadth.advancingVolume - breadth.decliningVolume;
        return cumulative;
      });
      return output;
    }

    let cumulative = 0;
    output.primary = candles.map((candle, index) => {
      if (index === 0) {
        return 0;
      }
      const previous = candles[index - 1].close;
      if (candle.close > previous) {
        cumulative += candle.volume;
      } else if (candle.close < previous) {
        cumulative -= candle.volume;
      }
      return cumulative;
    });
    return output;
  }

  if (key === 'openInterest') {
    output.primary = candles.map((candle) => {
      const value = candle.openInterest;
      return Number.isFinite(value) ? value : undefined;
    });
    if (!output.primary.some((value) => value !== undefined)) {
      output.primary = signedVolumeCumulative(candles);
    }
    output.secondary = emaMaybe(output.primary, length2);
    return output;
  }

  if (key === 'volumeDelta') {
    const sideBased = candles.map((candle) => {
      if (Number.isFinite(candle.buyVolume) && Number.isFinite(candle.sellVolume)) {
        return (candle.buyVolume ?? 0) - (candle.sellVolume ?? 0);
      }
      return undefined;
    });

    if (sideBased.some((value) => value !== undefined)) {
      output.primary = sideBased;
      output.histogram = sideBased;
      return output;
    }

    output.primary = candles.map((candle) => {
      const range = candle.high - candle.low;
      if (range === 0) {
        return 0;
      }
      const directionalPressure = (candle.close - candle.open) / range;
      return directionalPressure * candle.volume;
    });
    output.histogram = output.primary;
    return output;
  }

  if (key === 'multiTimePeriodCharts') {
    const fast = makeBucketCloseSeries(closes, Math.max(2, Math.round(length / 2)));
    const medium = makeBucketCloseSeries(closes, Math.max(3, length));
    const slow = makeBucketCloseSeries(closes, Math.max(5, length2));
    output.primary = sanitizeSeries(fast);
    output.secondary = sanitizeSeries(medium);
    output.tertiary = sanitizeSeries(slow);
    return output;
  }

  const definition = BASIC_INDICATOR_DEFINITIONS[key];
  const seed = seededUnit(key);
  const tr = trueRange(candles);
  const atrBase = rma(tr, Math.max(2, length2));
  const basis = ema(closes, Math.max(2, length));
  const fast = ema(closes, Math.max(2, Math.round(length / 2)));
  const highestHigh = highest(highs, length);
  const lowestLow = lowest(lows, length);
  const typical = candles.map((candle) => (candle.high + candle.low + candle.close) / 3);

  let primary: Array<number | undefined> = basis;
  let secondary: Array<number | undefined> | undefined;
  let tertiary: Array<number | undefined> | undefined;
  let histogram: Array<number | undefined> | undefined;

  if (key === 'autoFibRetracement') {
    const fib = makeFibLikeSeries(highs, lows, length, 'retracement');
    primary = fib.primary;
    secondary = fib.secondary;
    tertiary = fib.tertiary;
  } else if (key === 'autoFibExtension') {
    const fib = makeFibLikeSeries(highs, lows, length, 'extension');
    primary = fib.primary;
    secondary = fib.secondary;
    tertiary = fib.tertiary;
  } else if (key === 'robBookerZivGhostPivots') {
    const pivots = makeRobBookerGhostPivots(highs, lows, length);
    primary = pivots.primary;
    secondary = pivots.secondary;
    tertiary = pivots.tertiary;
  } else if (key === 'pivotPointsStandard') {
    const pivotLevels = computePivotPointsStandardLevels(candles, settings, context);
    output.pivotLevels = pivotLevels;
    primary = pivotLevels.p ?? new Array(candles.length).fill(undefined);
    secondary = pivotLevels.r1 ?? new Array(candles.length).fill(undefined);
    tertiary = pivotLevels.s1 ?? new Array(candles.length).fill(undefined);
  } else if (key === 'pivotPointsHighLow' || key === 'priceTarget') {
    primary = candles.map((candle, index) => {
      if (index === 0) {
        return undefined;
      }
      const prev = candles[index - 1];
      return (prev.high + prev.low + prev.close) / 3;
    });
    secondary = primary.map((pivot, index) => {
      if (pivot === undefined || index === 0) {
        return undefined;
      }
      return 2 * pivot - candles[index - 1].low;
    });
    tertiary = primary.map((pivot, index) => {
      if (pivot === undefined || index === 0) {
        return undefined;
      }
      return 2 * pivot - candles[index - 1].high;
    });
  } else if (key === 'autoPitchfork') {
    const pitchfork = makePitchforkSeries(highs, lows, length);
    primary = pitchfork.primary;
    secondary = pitchfork.secondary;
    tertiary = pitchfork.tertiary;
  } else if (key === 'linearRegressionChannel' || key === 'keltnerChannels' || key === 'envelope' || key === 'bollingerBars') {
    primary = basis;
    secondary = basis.map((value, index) => {
      const spread = atrBase[index];
      if (value === undefined || spread === undefined) {
        return undefined;
      }
      return value + spread * multiplier;
    });
    tertiary = basis.map((value, index) => {
      const spread = atrBase[index];
      if (value === undefined || spread === undefined) {
        return undefined;
      }
      return value - spread * multiplier;
    });
  } else if (key === 'autoTrendDetector') {
    primary = fast.map((value, index) => {
      const slow = basis[index];
      if (value === undefined || slow === undefined) {
        return undefined;
      }
      return value - slow;
    });
    secondary = emaMaybe(primary, length2);
  } else if (key === 'tradingSessions') {
    primary = sessionAverageSeries(candles, typical, true);
    secondary = primary.map((value, index) => {
      const spread = atrBase[index];
      if (value === undefined || spread === undefined) {
        return undefined;
      }
      return value + spread;
    });
    tertiary = primary.map((value, index) => {
      const spread = atrBase[index];
      if (value === undefined || spread === undefined) {
        return undefined;
      }
      return value - spread;
    });
  } else if (key === 'timeWeightedAveragePrice') {
    primary = sessionAverageSeries(candles, typical, false);
  } else if (key === 'visibleAveragePrice') {
    primary = visibleAverageSeries(candles, typical, Math.max(20, length * 3), context);
  } else if (key === 'moonPhases') {
    const synodicMonthDays = 29.530588853;
    const scale = ema(tr, Math.max(2, length2));
    primary = candles.map((candle, index) => {
      const amplitude = scale[index];
      const timestamp = parseCandleTime(candle);
      if (amplitude === undefined || !timestamp) {
        return undefined;
      }
      const cycle = timestamp.getTime() / 86_400_000 / synodicMonthDays;
      const angle = (cycle - Math.floor(cycle)) * Math.PI * 2;
      return closes[index] + Math.sin(angle) * amplitude * 0.5;
    });
    secondary = candles.map((candle, index) => {
      const amplitude = scale[index];
      const timestamp = parseCandleTime(candle);
      if (amplitude === undefined || !timestamp) {
        return undefined;
      }
      const cycle = timestamp.getTime() / 86_400_000 / synodicMonthDays;
      const angle = (cycle - Math.floor(cycle)) * Math.PI * 2;
      return closes[index] + Math.cos(angle) * amplitude * 0.5;
    });
  } else if (key === 'seasonality') {
    const seasonal = seasonalitySeries(candles, length2);
    primary = seasonal.primary;
    secondary = seasonal.secondary;
  } else if (key === 'technicalRatings') {
    const rsi = computeRsi(closes, length);
    const trend = fast.map((value, index) => {
      const slow = basis[index];
      if (value === undefined || slow === undefined) {
        return undefined;
      }
      return value - slow;
    });
    const trendScale = emaMaybe(trend, length2);
    primary = closes.map((_, index) => {
      const rsiValue = rsi[index];
      const trendValue = trendScale[index];
      const spread = atrBase[index];
      if (rsiValue === undefined || trendValue === undefined || spread === undefined || spread === 0) {
        return undefined;
      }
      const momentumScore = (rsiValue - 50) / 50;
      const trendScore = Math.max(-1, Math.min(1, trendValue / spread));
      return Math.max(-1, Math.min(1, momentumScore * 0.6 + trendScore * 0.4));
    });
    secondary = emaMaybe(primary, length3);
    tertiary = primary.map((value) => (value === undefined ? undefined : Math.sign(value)));
  } else if (key === 'zigZag') {
    primary = makeZigZagApprox(highs, lows, closes, length);
  } else {
    const variant = Math.floor(seed * 6);
    if (variant === 0) {
      primary = basis;
    } else if (variant === 1) {
      primary = closes.map((close, index) => {
        const center = basis[index];
        if (center === undefined) {
          return undefined;
        }
        return close - center;
      });
    } else if (variant === 2) {
      primary = closes.map((close, index) => {
        if (index < length) {
          return undefined;
        }
        const past = closes[index - length];
        const ratio = safeDivide(close - past, past);
        return ratio === undefined ? undefined : ratio * 100;
      });
    } else if (variant === 3) {
      primary = closes.map((close, index) => {
        const hi = highestHigh[index];
        const lo = lowestLow[index];
        if (hi === undefined || lo === undefined || hi === lo) {
          return undefined;
        }
        return ((close - lo) / (hi - lo)) * 100 - 50;
      });
    } else if (variant === 4) {
      primary = signedVolumeCumulative(candles);
    } else {
      const median = candles.map((candle) => (candle.open + candle.high + candle.low + candle.close) / 4);
      primary = sma(median, length);
    }
  }

  if (output.pivotLevels) {
    const sanitizedLevels: Partial<Record<PivotPointsLevel, Array<number | undefined>>> = {};
    for (const level of PIVOT_POINTS_LEVELS) {
      const values = output.pivotLevels[level];
      if (values) {
        sanitizedLevels[level] = sanitizeSeries(values);
      }
    }
    output.pivotLevels = sanitizedLevels;
  }

  output.primary = sanitizeSeries(primary);

  if (definition.series.secondary) {
    const fallbackSecondary = secondary ?? emaMaybe(output.primary, length2);
    output.secondary = sanitizeSeries(fallbackSecondary);
  }

  if (definition.series.tertiary) {
    const fallbackTertiary =
      tertiary ??
      output.primary.map((value, index) => {
        const spread = atrBase[index];
        if (value === undefined || spread === undefined) {
          return undefined;
        }
        return value - spread * multiplier * (0.4 + seed * 0.2);
      });
    output.tertiary = sanitizeSeries(fallbackTertiary);
  }

  if (definition.series.histogram) {
    const baseSecondary = definition.series.secondary
      ? output.secondary
      : sanitizeSeries(secondary ?? emaMaybe(output.primary, length2));
    const fallbackHistogram = histogram ?? seriesDifference(output.primary, baseSecondary);
    output.histogram = sanitizeSeries(fallbackHistogram);
  }

  return output;
}
